import { defineMessages, IntlShape } from 'react-intl'

import { FileValidationErrorCode } from '../healthPlan/utils/healthPlanCardValidation'

const documentErrorMessages = defineMessages({
  fileTypeNotAllowed: {
    defaultMessage: 'File type not supported. Supported formats: JPEG, PNG, PDF, and DOCX.',
    description: 'Error message when the uploaded file type is not allowed',
  },
  fileSizeExceeded: {
    defaultMessage: 'File size exceeds the 10 MB limit. Please choose a smaller file.',
    description: 'Error message when the uploaded file exceeds the size limit',
  },
})

export enum DocumentUploadErrorCode {
  FILE_NAME_ALREADY_EXISTS = 'FILE_NAME_ALREADY_EXISTS',
  FILE_TYPE_NOT_ALLOWED = 'FILE_TYPE_NOT_ALLOWED',
  FILE_SIZE_EXCEEDED = 'FILE_SIZE_EXCEEDED',
  INVALID_DOCUMENT_TYPE = 'INVALID_DOCUMENT_TYPE',
}

type DocumentUploadApiErrorData = {
  error_code?: string
  message?: string
}

type DocumentUploadApiError = {
  // Normalized shape from custom queryFn (e.g. uploadDocument)
  status?: number
  data?: DocumentUploadApiErrorData
  // Raw AxiosError shape from axiosBaseQuery (e.g. createDocumentFromTemplate)
  response?: {
    status?: number
    data?: DocumentUploadApiErrorData
  }
}

export const isFileNameAlreadyExistsError = (error: unknown): boolean => {
  const apiError = error as DocumentUploadApiError
  const errorData = apiError?.data ?? apiError?.response?.data
  const status = apiError?.status ?? apiError?.response?.status
  const errorCode = errorData?.error_code
  return (
    errorCode === DocumentUploadErrorCode.FILE_NAME_ALREADY_EXISTS ||
    (!errorCode && status === 400 && !!errorData?.message?.includes('already exists'))
  )
}

export const getDocumentUploadErrorMessage = (error: unknown, intl: IntlShape): string => {
  const apiError = error as DocumentUploadApiError
  const errorData = apiError?.data ?? apiError?.response?.data
  let errorCode = errorData?.error_code

  if (isFileNameAlreadyExistsError(error)) {
    errorCode = DocumentUploadErrorCode.FILE_NAME_ALREADY_EXISTS
  }

  switch (errorCode) {
    case DocumentUploadErrorCode.FILE_NAME_ALREADY_EXISTS:
      return intl.formatMessage({
        defaultMessage: 'A document with this name already exists. Please rename and try again.',
        description: 'Error message when a document with the same file name already exists',
      })
    case DocumentUploadErrorCode.FILE_TYPE_NOT_ALLOWED:
      return intl.formatMessage(documentErrorMessages.fileTypeNotAllowed)
    case DocumentUploadErrorCode.FILE_SIZE_EXCEEDED:
      return intl.formatMessage(documentErrorMessages.fileSizeExceeded)
    case DocumentUploadErrorCode.INVALID_DOCUMENT_TYPE:
      return intl.formatMessage({
        defaultMessage: 'Invalid document type. Please select a valid type.',
        description: 'Error message when an invalid document type is submitted',
      })
    default:
      return intl.formatMessage({
        defaultMessage: 'Failed to upload document. Please try again.',
        description: 'Generic error message when document upload fails',
      })
  }
}

export const getFileValidationErrorMessage = (
  errorCode: FileValidationErrorCode | undefined,
  intl: IntlShape,
): string => {
  switch (errorCode) {
    case FileValidationErrorCode.FILE_TOO_LARGE:
      return intl.formatMessage(documentErrorMessages.fileSizeExceeded)
    case FileValidationErrorCode.UNSUPPORTED_FILE_TYPE:
      return intl.formatMessage(documentErrorMessages.fileTypeNotAllowed)
    default:
      return intl.formatMessage({
        defaultMessage: 'Failed to upload document. Please try again.',
        description: 'Generic error message when document upload fails',
      })
  }
}
